/**
 * Sends the Meta Pixel (fbq) events not already covered elsewhere:
 * - AddToCart: derived from Horizon's CartLinesUpdateEvent, which both the
 *   PDP form and quick-add dispatch through the same ProductFormComponent
 *   (see assets/product-form.js), so this single listener covers both.
 * - InitiateCheckout: fired when the InfinitePay checkout button is
 *   clicked (see assets/infinitepay-checkout.js for the rest of that flow).
 * PageView is tracked directly in layout/theme.liquid; Purchase is tracked
 * in sections/infinitepay-order-confirmation.liquid and the InfinitePay
 * worker. See the comment in layout/theme.liquid for why these are tracked
 * explicitly instead of relying on another integration.
 */
import { StandardEvents } from '@shopify/events';

function track(eventName, data) {
  if (typeof fbq !== 'function') return;
  fbq('track', eventName, data);
}

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  if (event.action !== 'add') return;

  // event.lines is the synchronous list of what this specific action added
  // (merchandiseId is a GID, e.g. "gid://shopify/ProductVariant/123"); detail.items
  // only arrives once the fetch resolves and reflects the whole cart (with bare
  // numeric ids), so it's filtered down to match by the numeric id.
  const addedMerchandiseIds = new Set(
    event.lines.map((line) => String(line.merchandiseId).split('/').pop())
  );

  event.promise
    ?.then(({ detail }) => {
      if (!detail || detail.didError) return;

      const addedItems = (detail.items || []).filter((item) => addedMerchandiseIds.has(String(item.id)));
      if (addedItems.length === 0) return;

      track('AddToCart', {
        content_ids: addedItems.map((item) => String(item.product_id)),
        content_type: 'product',
        contents: addedItems.map((item) => ({ id: String(item.product_id), quantity: item.quantity })),
        value: addedItems.reduce((sum, item) => sum + (item.price / 100) * item.quantity, 0),
        currency: 'BRL',
      });
    })
    .catch(() => {
      // tracking failure shouldn't surface to the shopper
    });
});

document.addEventListener('click', async (event) => {
  const button = event.target instanceof Element ? event.target.closest('#infinitepay-checkout') : null;
  // #infinitepay-checkout is a plain <a> (see snippets/cart-summary.liquid),
  // not a <button> - the empty-cart state is expressed as aria-disabled
  // since anchors have no native `disabled`.
  if (!button || button.disabled || button.getAttribute('aria-disabled') === 'true') return;

  try {
    const response = await fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) return;
    const cart = await response.json();
    if (!cart.items || cart.items.length === 0) return;

    track('InitiateCheckout', {
      content_ids: cart.items.map((item) => String(item.product_id)),
      content_type: 'product',
      contents: cart.items.map((item) => ({ id: String(item.product_id), quantity: item.quantity })),
      num_items: cart.item_count,
      value: cart.total_price / 100,
      currency: 'BRL',
    });
  } catch {
    // tracking failure shouldn't block checkout
  }
});
